import axios from 'axios';
import { toast } from 'react-toastify';

const SESSION_LOCK_STORAGE_KEY = 'app_session_lock_state_v1';

const readSessionLockState = () => {
  try {
    const raw = localStorage.getItem(SESSION_LOCK_STORAGE_KEY);
    if (!raw) return { isLocked: false };
    const state = JSON.parse(raw);
    return { isLocked: Boolean(state?.isLocked) };
  } catch {
    return { isLocked: false };
  }
};

let waitForUnlockPromise = null;
const waitForUnlock = () => {
  if (!readSessionLockState().isLocked) {
    return Promise.resolve();
  }
  if (waitForUnlockPromise) {
    return waitForUnlockPromise;
  }

  waitForUnlockPromise = new Promise((resolve, reject) => {
    const start = Date.now();
    const timeoutMs = 10 * 60 * 1000;

    const cleanup = () => {
      if (intervalId) clearInterval(intervalId);
      window.removeEventListener('storage', onStorage);
      waitForUnlockPromise = null;
    };

    const tick = () => {
      if (!readSessionLockState().isLocked) {
        cleanup();
        resolve();
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        cleanup();
        const err = new Error('Timeout attesa sblocco sessione');
        err.code = 'SESSION_LOCK_TIMEOUT';
        reject(err);
      }
    };

    const onStorage = (event) => {
      if (event?.key === SESSION_LOCK_STORAGE_KEY) {
        tick();
      }
    };

    window.addEventListener('storage', onStorage);
    const intervalId = setInterval(tick, 500);
    tick();
  });

  return waitForUnlockPromise;
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor for adding token to headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    try {
      if (typeof FormData !== 'undefined' && config?.data instanceof FormData) {
        if (config.headers) {
          const headers = config.headers;
          if (typeof headers.delete === 'function') {
            headers.delete('Content-Type');
            headers.delete('content-type');
          } else {
            delete headers['Content-Type'];
            delete headers['content-type'];
          }
        }
      }
    } catch {
    }
    try {
      if (!token) {
        return config;
      }
      const url = config.url || '';
      if (url && url.startsWith('/session-lock')) {
        return config;
      }
      if (readSessionLockState().isLocked) {
        const err = new Error('Sessione bloccata per inattività');
        err.code = 'SESSION_LOCKED';
        err.isSessionLocked = true;
        err.silent = true;
        err.config = config;
        return Promise.reject(err);
      }
    } catch {
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.isSessionLocked || error?.code === 'SESSION_LOCKED') {
      const originalConfig = error?.config;
      if (!originalConfig) {
        return Promise.reject(error);
      }
      return waitForUnlock().then(() => api.request(originalConfig));
    }
    if (error.response) {
      if (error.response.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else if (
        error.response.status === 403 &&
        error.response.data &&
        error.response.data.message === 'Accesso negato: Indirizzo IP non autorizzato per lo Smart Working'
      ) {
        const blockedIp = error.response.data.ip;
        const ipText = blockedIp ? ` (IP rilevato: ${blockedIp})` : '';
        toast.error(
          `Accesso negato per Smart Working. Configura questo IP nelle impostazioni dell’utente${ipText}`
        );
      }
    }
    return Promise.reject(error);
  }
);

export default api;
